import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { AxiosResponse } from 'axios';
import { useHistory } from 'react-router-dom';

import {
  getDetailOrderApi,
  pendingCountdownOrderApi,
} from 'app/services/checkoutApi';
import { I_TicketData, I_TicketDataProcessed } from '../types';
import {
  checkInventoryApi,
  getInventoryApi,
  updateOrderApi,
} from 'app/services/orderApi';
import { generateTicketIdentify } from 'app/pages/Cart/utils';
import { PaymentMethod } from 'app/pages/Cart/hooks/useCartNew';
import { CART_ERROR_TYPE } from '../components/TicketItem';
import { Park } from 'app/pages/Cart/slice/types';
import { formatTimeFrame, generateUUID } from 'utils/helper';
import { TICKET_TYPES } from 'utils/constants';

export interface I_TicketItemByPark {
  park: Park;
  tickets: I_TicketDataProcessed[];
}

export interface I_UpdateOrderData {
  show_id: number;
  amount: number;
  used_area2?: number;
  date: string;
  zone_id?: number;
  restaurant_id?: number;
}

export const checkZoneId = zoneId => {
  if (zoneId === 'null' || zoneId === 'undefined') return undefined;
  return zoneId;
};

export const checkPerfomanceId = performanceId => {
  if (performanceId === 'null' || performanceId === 'undefined')
    return undefined;
  return performanceId;
};

export interface I_PaymentInfo {
  code: string;
  id: number;
  mapVGS: string;
  name: string;
}

export interface I_DeliverTicketType {
  code: null | string;
  id: number;
  name: string;
}

export type OnChangeTicketAmount = (
  identify: string,
  newAmount: number,
) => void;

export type OnGetTicketsPrice = (arg: {
  paymentMethod: PaymentMethod;
  bpAllocatedDebt?: number | null;
  onSuccess?: (listCartToGetPrice?: any) => void;
  onError?: (listCartToGetPrice?: any) => void;
  onCompleted?: () => void;
  onStartApi?: () => void;
}) => void;

export interface OnCheckInventoryArgs {
  ticketsData: I_TicketData[];
  paymentMethod: PaymentMethod;
  bpAllocatedDebt?: number | null;
  onSuccess?: (responseData) => void;
  onError?: (error) => void;
  onCompleted?: () => void;
}

export type OnUpdateOrder = (
  paymentMethod: number,
  deliverTicketId: number,
  dataToUpdate: I_UpdateOrderData[],
) => Promise<AxiosResponse<any, any>>;

export type OnUpdateTicket = (
  identify: string,
  dataToUpdate: Partial<I_TicketDataProcessed>,
) => void;

export type OnUpdateTicketList = (
  dataToUpdate: I_TicketDataProcessed[],
) => void;

const useOrder = (orderHashId: string) => {
  const [orderDetailData, setOrderDetailData] = useState<Record<string, any>>();
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [countDownId, setCountDownId] = useState(generateUUID());
  const [isGetOrderDataFailed, setIsGetOrderDataFailed] = useState(false);
  const getPriceTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const history = useHistory();
  const [originOrderDetailData, setOriginOrderDetailData] = useState({});

  const orderId = useMemo(
    () => orderDetailData?.id as string,
    [orderDetailData?.id],
  );

  const orderIdHash = useMemo(
    () => orderDetailData?.idHash as string,
    [orderDetailData?.idHash],
  );

  const ticketList = useMemo(
    () => (orderDetailData?.items as I_TicketDataProcessed[]) ?? [],
    [orderDetailData?.items],
  );

  const ticketsSortedByPark = useMemo(() => {
    const result: I_TicketItemByPark[] = [];
    if (ticketList?.length < 1 || !ticketList) return result;

    ticketList.forEach(ticket => {
      const {
        show: {
          park: { id: parkId },
        },
      } = ticket;

      const indexParkExists = result.findIndex(i => i.park.id === parkId);
      ticket.usedAreas2 = (ticket.usedAreas2 ?? []).map(item => ({
        label: formatTimeFrame(item.label),
        value: item.value,
      }));

      if (indexParkExists > -1) {
        result[indexParkExists].tickets.push(ticket);
        return true;
      }
      result.push({
        park: ticket.show.park,
        tickets: [ticket],
      });
    });

    return result;
  }, [ticketList]);

  const getInventory = useCallback(
    async (
      ticketList: I_TicketData[],
      paymentMethod: number,
      bpAllocatedDebt: number | null,
    ) => {
      const formData = new FormData();

      ticketList.forEach((item, idx) => {
        formData.append(`items[${idx}][date]`, item.date.dateOrigin);
        formData.append(`items[${idx}][zone_id]`, item.zone?.id ?? '');
        formData.append(`items[${idx}][show_id]`, item.show.id);
        formData.append(
          `items[${idx}][restaurant_id]`,
          item?.restaurant?.id ? item.restaurant.id.toString() : '',
        );
        formData.append(
          `items[${idx}][used_area2]`,
          item?.product?.usedArea2Object?.value ?? '',
        );
        formData.append(`items[${idx}][payment]`, paymentMethod.toString());
        if (!!bpAllocatedDebt)
          formData.append(
            `items[${idx}][bpAllocatedDebt]`,
            bpAllocatedDebt.toString(),
          );
      });

      try {
        const response = await getInventoryApi(formData);

        return response.data.reduce((result, i) => {
          const identify = generateTicketIdentify({
            showId: i.show_id,
            performanceId: i.performance,
            zoneId: i.zone_id,
            date: i.date,
            restaurantId: i?.restaurant_id,
            usedArea2: i?.used_area2,
          });

          result[identify] = i;
          return result;
        }, {});
      } catch (error) {
        return [];
      } finally {
      }
    },
    [],
  );

  const onChangeTicketAmount = useCallback<OnChangeTicketAmount>(
    (identify, newAmount) => {
      setCountDownId(generateUUID());
      setOrderDetailData(prev => {
        const _ticketList = (prev?.items as I_TicketDataProcessed[]) ?? [];
        const newTicketList = _ticketList.map(ticket => {
          if (ticket.identify !== identify) return { ...ticket };
          return { ...ticket, amount: newAmount };
        });
        return { ...prev, items: newTicketList };
      });
    },
    [],
  );

  const paymentInfo = useMemo<I_PaymentInfo>(() => {
    return orderDetailData?.payment ?? {};
  }, [orderDetailData?.payment]);

  const deliverTicketInfo = useMemo<I_DeliverTicketType>(() => {
    return orderDetailData?.deliverTicketType ?? {};
  }, [orderDetailData?.deliverTicketType]);

  const totalNumberTickets = useMemo(() => {
    return ticketList.reduce((result, ticket) => {
      return (result += ticket.amount);
    }, 0);
  }, [ticketList]);

  const totalPrice = useMemo(() => {
    return ticketList.reduce(
      (result, ticket) => (result += ticket.originalPrice * ticket.amount),
      0,
    );
  }, [ticketList]);

  const onGetTicketsPrice = useCallback<OnGetTicketsPrice>(
    async ({
      paymentMethod,
      bpAllocatedDebt,
      onSuccess,
      onError,
      onStartApi,
    }) => {
      const listCartToGetPrice = ticketList.filter(i => {
        const isNotApplyZone =
          i?.show?.zones?.length === 0 ||
          i?.show?.materialType === TICKET_TYPES.CABLE_CAR;
        const isNotApplyUsedArea2 =
          i?.show?.materialType === TICKET_TYPES.CABLE_CAR;
        const isRestaurantTicket =
          i?.show?.restaurants?.length > 0 &&
          i?.show?.materialType !== TICKET_TYPES.CABLE_CAR;

        if (
          (!isNotApplyUsedArea2 &&
            !i?.product?.usedArea2 &&
            !i?.product?.usedArea2Object?.value) ||
          (!isNotApplyZone && !i?.zone) ||
          (isRestaurantTicket && !i?.restaurant?.id)
        )
          return false;

        return true;
      });

      if (!listCartToGetPrice?.length) return;

      if (getPriceTimeoutRef.current) {
        clearTimeout(getPriceTimeoutRef.current);
      }
      getPriceTimeoutRef.current = setTimeout(async () => {
        onStartApi?.();

        const dataToGetPrice = listCartToGetPrice.map(i => ({
          item_id: i.id,
          amount: i.amount,
          show_id: i.show.id,
          date: i.date?.dateOrigin,
          zone_id: i.zone?.id,
          restaurant_id: i.restaurant?.id,
          used_area2: i?.product?.usedArea2Object?.value,
        }));

        const formData = new FormData();
        formData.append('idHash', orderIdHash);
        formData.append('payment_method', (paymentMethod ?? '').toString());
        if (bpAllocatedDebt)
          formData.append('bpAllocatedDebt', bpAllocatedDebt.toString());
        dataToGetPrice.forEach((i, idx) => {
          for (let key in i) {
            const val = i[key];
            if (!!val || val === 0)
              formData.append(`items[${idx}][${key}]`, i[key]);
          }
        });

        try {
          const response = await checkInventoryApi(formData);
          if (response.status === 200) {
            const responseAsObject: Record<string, Record<string, any>> = {};

            response?.data?.forEach(item => {
              const identify = generateTicketIdentify({
                showId: item?.show_id,
                performanceId: item?.performance,
                zoneId: item?.zone_id,
                date: item?.date,
                restaurantId: item?.restaurant?.id ?? item?.restaurant_id,
                usedArea2: item?.used_area2,
              });
              responseAsObject[identify] = item;
            });

            setOrderDetailData(prev => {
              const _ticketList =
                (prev?.items as I_TicketDataProcessed[]) ?? [];
              const newTicketList = _ticketList.map(ticket => {
                const ticketResponseData = responseAsObject?.[ticket?.identify];
                if (ticketResponseData) {
                  const newInventory = +(ticketResponseData?.inventory ?? 0);
                  const err = ticketResponseData?.error;
                  return {
                    ...ticket,
                    inventory: newInventory,
                    product: {
                      ...ticket.product,
                      originalPrice: ticketResponseData?.originalPrice,
                      isLongTerm: !!ticketResponseData?.isLongTerm,
                    },
                    originalPrice: ticketResponseData?.price,
                    errorTime:
                      ticketResponseData?.errorTime ??
                      (err?.errorType === CART_ERROR_TYPE.ERROR_TIME
                        ? err?.message
                        : ''),
                    errorTicket:
                      ticketResponseData?.errorTicket ??
                      (err?.errorType === CART_ERROR_TYPE.ERROR_TICKET
                        ? err?.message
                        : ''),
                  };
                }
                return { ...ticket };
              });
              return { ...prev, items: newTicketList };
            });

            onSuccess?.(listCartToGetPrice);
          }
        } catch (err: any) {
          const dataRequest = err?.data?.dataRequest;

          if (Array.isArray(dataRequest)) {
            const errorAsObject: Record<string, Record<string, any>> = {};
            dataRequest.forEach(item => {
              const identify = generateTicketIdentify({
                showId: item?.show_id,
                performanceId: item?.performance,
                zoneId: item?.zone_id,
                date: item?.date,
                restaurantId: item?.restaurant_id,
                usedArea2: item?.used_area2,
              });
              errorAsObject[identify] = item;
            });

            const newOrderDetailDataWithError = ticketList.map(ticket => {
              const errorResponseData =
                errorAsObject?.[(ticket as any)?.identify];

              if (errorResponseData) {
                const newJumpValue = +(errorResponseData?.jumpTicket ?? 0);
                const newMinAmount = +(errorResponseData?.minAmount ?? 0);
                const errMsg = errorResponseData?.message;
                const errCode = errorResponseData?.errorCode;
                return {
                  ...ticket,
                  errorTicket: errMsg,
                  jumpTicket:
                    errCode === 'jumpTicket'
                      ? newJumpValue
                      : (ticket as any).jumpTicket,
                  minAmountTicket:
                    errCode === 'minTicket'
                      ? newMinAmount
                      : (ticket as any).minAmountTicket,
                };
              }
              return { ...ticket };
            });

            setOrderDetailData(prev => {
              return {
                ...prev,
                items: newOrderDetailDataWithError,
              };
            });
          }

          onError?.(listCartToGetPrice);
        } finally {
        }
      }, 300);
    },
    [orderIdHash, ticketList],
  );

  const onCheckInventory = useCallback(
    async ({
      ticketsData,
      paymentMethod,
      onSuccess,
      onError,
      onCompleted,
      bpAllocatedDebt,
    }: OnCheckInventoryArgs) => {
      const listCartToGetPrice = ticketsData.filter(i => {
        const isNotApplyZone =
          i?.show?.zones?.length === 0 ||
          i?.show?.materialType === TICKET_TYPES.CABLE_CAR;
        const isNotApplyUsedArea2 =
          i?.show?.materialType === TICKET_TYPES.CABLE_CAR;
        const isRestaurantTicket =
          i?.show?.restaurants?.length > 0 &&
          i?.show?.materialType !== TICKET_TYPES.CABLE_CAR;

        if (
          (!isNotApplyUsedArea2 &&
            !i?.product?.usedArea2 &&
            !i?.product?.usedArea2Object?.value) ||
          (!isNotApplyZone && !i?.zone) ||
          (isRestaurantTicket && !i?.restaurant?.id)
        )
          return false;

        return true;
      });

      const dataToGetPrice = listCartToGetPrice.map(i => ({
        item_id: i.id,
        amount: i.amount,
        show_id: i.show.id,
        date: i.date?.dateOrigin,
        zone_id: i.zone?.id,
        restaurant_id: i.restaurant?.id,
        used_area2: i?.product?.usedArea2Object?.value,
      }));

      const formData = new FormData();
      formData.append('idHash', orderIdHash);
      formData.append('payment_method', (paymentMethod ?? '').toString());
      if (!!bpAllocatedDebt)
        formData.append('bpAllocatedDebt', bpAllocatedDebt.toString());
      dataToGetPrice.forEach((i, idx) => {
        for (let key in i) {
          const val = i[key];
          if (!!val || val === 0)
            formData.append(`items[${idx}][${key}]`, i[key]);
        }
      });

      try {
        const response = await checkInventoryApi(formData);
        onSuccess?.(response.data);
        return response.data;
      } catch (error) {
        onError?.(error);
      } finally {
        onCompleted?.();
      }
    },
    [orderIdHash],
  );

  const pendingCountdownOrder = useCallback(
    async (orderHashId: string, pending: '0' | '1') => {
      const pendingData = new FormData();
      pendingData.append('idHash', orderHashId);
      pendingData.append('pending', pending);
      await pendingCountdownOrderApi(pendingData);
    },
    [],
  );

  const onUpdateTicket = useCallback<OnUpdateTicket>(
    (identify, dataToUpdate) => {
      setOrderDetailData(prev => {
        const _ticketList = (prev?.items as I_TicketDataProcessed[]) ?? [];
        const newTicketList = _ticketList.map(ticket => {
          if (ticket.identify !== identify) return { ...ticket };
          return { ...ticket, ...dataToUpdate };
        });
        return { ...prev, items: newTicketList };
      });
    },
    [],
  );

  const onUpdateOrder = useCallback<OnUpdateOrder>(
    async (paymentMethod, deliverTicketId, dataToUpdate) => {
      // if (!executeRecaptcha) return;
      // const tokenCaptcha = await executeRecaptcha('updateOrder');
      const formData = new FormData();
      // formData.append('captcha', tokenCaptcha);
      formData.append('idHash', orderIdHash);
      formData.append('payment_method', paymentMethod.toString());
      formData.append('deliver_ticket_id', deliverTicketId.toString());
      dataToUpdate.forEach((ticket, idx) => {
        for (let key in ticket) {
          const value = ticket[key];
          if (value !== null && value !== undefined) {
            formData.append(`items[${idx}][${key}]`, value);
          }
        }
      });

      try {
        const response = await updateOrderApi(formData);
        return response;
      } catch (error: any) {
        if (error?.code === 400 && error?.data?.errorType === 'errorTicket') {
          const ticketIdentify = orderDetailData?.items?.find(
            v => v.id === error?.data?.itemId,
          )?.identify;

          onUpdateTicket(ticketIdentify, {
            errorTicket: error?.data?.message ?? '',
          });
        }
        throw error;
      }
    },
    [onUpdateTicket, orderDetailData, orderIdHash],
  );

  const onUpdateTicketList = useCallback<OnUpdateTicketList>(dataToUpdate => {
    setOrderDetailData(prev => {
      return { ...prev, items: dataToUpdate };
    });
  }, []);

  const onAddingTicket = useCallback(
    async (
      addingData: Record<string, Partial<I_TicketData>>,
      paymentMethod: PaymentMethod,
      bpAllocatedDebt?: number | null,
    ) => {
      const _addingData = { ...addingData };

      const combineTicketSameData = (() => {
        const _ticketList = ticketList ?? [];
        const combineTicketSameData = _ticketList.map(ticket => {
          const ticketIdentify = ticket.identify;
          const itemAddingSameData = _addingData[ticketIdentify];

          if (!itemAddingSameData) return ticket;

          delete _addingData[ticketIdentify];
          const newAmount = +ticket.amount + +(itemAddingSameData?.amount ?? 0);

          return {
            ...ticket,
            amount: newAmount,
            actualAmountInOrder:
              (ticket as any)?.actualAmountInOrder ?? +ticket.amount,
            price: newAmount * ticket.price,
          };
        });
        return combineTicketSameData;
      })();

      const newOrderDetailData = [
        ...Object.values(_addingData),
        ...combineTicketSameData,
      ] as I_TicketData[];

      const checkInventorySuccess = inventoryData => {
        const responseAsObject: Record<string, Record<string, any>> = {};
        inventoryData.forEach(item => {
          const identify = generateTicketIdentify({
            showId: item?.show_id,
            performanceId: item?.performance,
            zoneId: item?.zone_id,
            date: item?.date,
            restaurantId: item?.restaurant?.id ?? item?.restaurant_id,
            usedArea2: item?.used_area2,
          });
          responseAsObject[identify] = item;
        });

        const newOrderDetailDataWithInventory = newOrderDetailData.map(
          ticket => {
            const ticketResponseData =
              responseAsObject?.[(ticket as any)?.identify];

            if (ticketResponseData) {
              const newInventory = +(ticketResponseData?.inventory ?? 0);
              const err = ticketResponseData?.error;
              return {
                ...ticket,
                inventory: newInventory,
                product: {
                  ...ticket.product,
                  originalPrice: ticketResponseData?.originalPrice,
                  isLongTerm:
                    !!ticketResponseData?.isLongTerm ||
                    !!ticket?.product?.isLongTerm,
                },
                originalPrice: ticketResponseData?.price,
                errorTime:
                  ticketResponseData?.errorTime ??
                  (err?.errorType === CART_ERROR_TYPE.ERROR_TIME
                    ? err?.message
                    : ''),
                errorTicket:
                  ticketResponseData?.errorTicket ??
                  (err?.errorType === CART_ERROR_TYPE.ERROR_TICKET
                    ? err?.message
                    : ''),
              };
            }
            return { ...ticket };
          },
        );

        setOrderDetailData(prev => {
          return {
            ...prev,
            items: newOrderDetailDataWithInventory,
          };
        });
      };

      const checkInventoryError = error => {
        const dataRequest = error?.data?.dataRequest;

        if (Array.isArray(dataRequest)) {
          const errorAsObject: Record<string, Record<string, any>> = {};
          dataRequest.forEach(item => {
            const identify = generateTicketIdentify({
              showId: item?.show_id,
              performanceId: item?.performance,
              zoneId: item?.zone_id,
              date: item?.date,
              restaurantId: item?.restaurant_id,
              usedArea2: item?.used_area2,
            });
            errorAsObject[identify] = item;
          });

          const newOrderDetailDataWithError = newOrderDetailData.map(ticket => {
            const errorResponseData =
              errorAsObject?.[(ticket as any)?.identify];

            if (errorResponseData) {
              const newJumpValue = +(errorResponseData?.jumpTicket ?? 0);
              const newMinAmount = +(errorResponseData?.minAmount ?? 0);
              const errMsg = errorResponseData?.message;
              const errCode = errorResponseData?.errorCode;
              return {
                ...ticket,
                errorTicket: errMsg,
                jumpTicket:
                  errCode === 'jumpTicket'
                    ? newJumpValue
                    : (ticket as any).jumpTicket,
                minAmountTicket:
                  errCode === 'minTicket'
                    ? newMinAmount
                    : (ticket as any).minAmountTicket,
              };
            }
            return { ...ticket };
          });

          setOrderDetailData(prev => {
            return {
              ...prev,
              items: newOrderDetailDataWithError,
            };
          });
        }
      };

      await onCheckInventory({
        ticketsData: newOrderDetailData,
        paymentMethod,
        onSuccess: checkInventorySuccess,
        onError: checkInventoryError,
        bpAllocatedDebt,
      });
    },
    [onCheckInventory, ticketList],
  );

  const onRemoveTicket = useCallback((identify: string) => {
    setIsLoadingData(true);
    setOrderDetailData(prev => {
      const _ticketList = (prev?.items as I_TicketDataProcessed[]) ?? [];

      const newTicketList = _ticketList.filter(
        item => item.identify !== identify,
      );

      return {
        ...prev,
        items: newTicketList,
      };
    });
    setIsLoadingData(false);
  }, []);

  const onSetTicket = useCallback(
    (setter: (prev: I_TicketDataProcessed[]) => I_TicketDataProcessed[]) => {
      setOrderDetailData(prev => {
        const _ticketList = (prev?.items as I_TicketDataProcessed[]) ?? [];

        const newTicketList = setter(_ticketList);

        return {
          ...prev,
          items: newTicketList,
        };
      });
    },
    [],
  );

  useEffect(() => {
    const getOrderDetailData = async () => {
      try {
        setIsLoadingData(true);
        await pendingCountdownOrder(orderHashId, '1');
        const response = await getDetailOrderApi(orderHashId, true);
        const data = response.data;

        sessionStorage.setItem(
          'prevTicketType',
          sessionStorage.getItem('ticketType') || '',
        );

        sessionStorage.setItem(
          'ticketType',
          JSON.stringify(!!data?.isLongTerm ? 1 : 0),
        );

        if (
          data?.orderState?.value !== 'paymentPending' &&
          data?.orderState?.value !== 'cancelled'
        ) {
          history.goBack();
        } else if (data?.orderType?.value === 'upgrade') {
          history.replace('/page-not-found');
        }
        const ticketList: I_TicketData[] = data?.items;
        const inventoryData = await getInventory(
          ticketList,
          data.payment.id,
          data?.businessPartnerAllocatedDebt?.id,
        );
        const ticketListWithInventory = ticketList.map(ticket => {
          const ticketIdentify = generateTicketIdentify({
            showId: ticket.show?.id,
            performanceId: undefined,
            zoneId: ticket.zone?.id,
            date: ticket.date?.dateOrigin,
            restaurantId: ticket?.restaurant?.id?.toString(),
            usedArea2: ticket?.product?.usedArea2Object?.value,
          });
          const dataInventory = inventoryData[ticketIdentify];
          if (dataInventory) {
            return {
              ...ticket,
              inventory: +(dataInventory?.inventory ?? 0),
              identify: ticketIdentify,
              key: ticketIdentify + Date.now(),
              product: {
                ...ticket?.product,
                isLongTerm: dataInventory?.isLongTerm,
              },
            };
          }
          return {
            ...ticket,
            identify: ticketIdentify,
            key: ticketIdentify + Date.now(),
          };
        });

        data.items = ticketListWithInventory;

        setOrderDetailData(data);
        setOriginOrderDetailData(data);
      } catch (err) {
        setIsGetOrderDataFailed(true);
      } finally {
        setIsLoadingData(false);
      }
    };

    getOrderDetailData();
  }, [getInventory, history, orderHashId, pendingCountdownOrder]);

  return {
    orderId,
    orderIdHash,
    ticketList,
    paymentInfo,
    totalNumberTickets,
    ticketsSortedByPark,
    onChangeTicketAmount,
    totalPrice,
    onGetTicketsPrice,
    deliverTicketInfo,
    onUpdateOrder,
    isLoadingData,
    onUpdateTicket,
    onUpdateTicketList,
    countDownId,
    setCountDownId,
    pendingCountdownOrder,
    orderDetailData,
    isGetOrderDataFailed,
    onAddingTicket,
    onRemoveTicket,
    onSetTicket,
    setOriginOrderDetailData,
    originOrderDetailData,
    setOrderDetailData,
  };
};

export default useOrder;
